import { gql } from '@apollo/client';
import invariant from 'invariant';
import memoize from 'memoizee';

import isAPIError from 'lib/isAPIError';
import { getApolloClientSingleton } from 'lib/apollo/client/browser';
import { dispatcher } from 'lib/Dispatcher';
import ChatMessages from 'collections/ChatMessages';
import { ChatGapMessage, PhoneCallMessage, ChatMessage } from 'entities/ChatMessage';
import { ActionTypes, DATA_RELOAD_TIMEOUT_MS } from 'chat/constants/ChatConstants';
import { saveReactionSummary } from 'chat/components/Reaction';
import ToastActions from 'actions/ToastActions';
import { MessageTranslationsInput } from '../../../__generated__/globalTypes';

/* This cache write is mainly for the chat message list.
 * This should cache all the neccesary data for PhoneCallMessageItemNote
 */
export function writeApolloCacheForChatMessages(messages: any) {
  // @ts-expect-error ts-migrate(7006) FIXME: Parameter 'message' implicitly has an 'any' type.
  messages.forEach((message) => {
    if (message.getReactionSummary) {
      const reaction = message.getReactionSummary().toGraphQL();

      if (reaction.messageUuid) {
        try {
          saveReactionSummary(reaction);
        } catch (e) {
          console.error('could not write reaction in chat message cache', message.id);
        }
      }
    }
  });

  // @ts-expect-error ts-migrate(7006) FIXME: Parameter 'm' implicitly has an 'any' type.
  const phoneCallMessages = messages.filter((m) => m instanceof PhoneCallMessage);
  // @ts-expect-error ts-migrate(7006) FIXME: Parameter 'm' implicitly has an 'any' type.
  const phoneCalls = phoneCallMessages.map((m) => m.getCall());

  // We loop and write the query with single uuid because
  // the variables needs to be identicals to the real queries.
  // If we write to the cache a query with the following variables uuids = [1, 2, 3]
  // then a component building a query with the following variables = [1] will not read the cache.
  // @ts-expect-error ts-migrate(7006) FIXME: Parameter 'phoneCall' implicitly has an 'any' type... Remove this comment to see the full error message
  phoneCalls.forEach((phoneCall) => {
    try {
      getApolloClientSingleton().writeQuery({
        query: gql`
          query getPhoneCall($uuid: String!) {
            phoneCalls(uuids: [$uuid]) {
              uuid
              note
              reasons
              status
              duration
              callee_uuid
              caller_uuid
            }
          }
        `,
        variables: { uuid: phoneCall.uuid },
        data: {
          phoneCalls: [
            {
              __typename: 'PhoneCall',
              uuid: phoneCall.uuid,
              note: phoneCall.note,
              reasons: phoneCall.reasons,
              status: phoneCall.status,
              duration: phoneCall.duration,
              callee_uuid: phoneCall.callee_uuid,
              caller_uuid: phoneCall.caller_uuid,
            },
          ],
        },
      });
    } catch (e) {
      console.error('could not write phone call message in chat message cache', phoneCall.uuid);
    }
  });
}

const ChatMessageActions = {
  sendMessage(message: ChatMessage, translations?: MessageTranslationsInput) {
    dispatcher.dispatch({
      type: ActionTypes.SEND_MESSAGE,
      chatId: message.getChatId(),
      message,
    });

    const onSuccess = () => {
      dispatcher.dispatch({
        type: ActionTypes.SENT_MESSAGE,
        message,
      });
    };

    // @ts-expect-error ts-migrate(7006) FIXME: Parameter 'err' implicitly has an 'any' type.
    const onError = (err) => {
      dispatcher.dispatch({
        type: ActionTypes.MESSAGE_FAILED,
        message,
      });
      throw err;
    };

    return message.send(translations).then(onSuccess, onError);
  },

  removeMessage(message: ChatMessage) {
    dispatcher.dispatch({
      type: ActionTypes.REMOVE_MESSAGE,
      message,
    });
  },

  loadMissingChatMessages(
    chatId: Backbone$Id,
    sinceSeq: number | undefined | null = null,
    maxSeq: number | undefined | null = null
  ) {
    const data = {};
    if (sinceSeq) {
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'since_seq' does not exist on type '{}'.
      data.since_seq = sinceSeq;
    }
    if (maxSeq) {
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'max_seq' does not exist on type '{}'.
      data.max_seq = maxSeq;
    }

    return ChatMessages.yieldMessagesForChatId(chatId, { data }).then((messages) => {
      writeApolloCacheForChatMessages(messages);
      dispatcher.dispatch({
        type: ActionTypes.LOAD_MISSING_CHAT_MESSAGES,
        chatId,
        messages,
      });
    });
  },

  loadChatMessages(chatId: Backbone$Id): void {
    invariant(chatId, 'chatId was missing in loadChatMessages');

    ChatMessages.yieldMessagesForChatId(chatId)
      .then((messages) => {
        writeApolloCacheForChatMessages(messages);
        dispatcher.dispatch({
          type: ActionTypes.LOAD_CHAT_MESSAGES,
          chatId,
          messages,
        });
      })
      .catch((e) => {
        console.warn(e, e && e.stack);

        if (!isAPIError(e)) {
          console.error('ChatMessages.yieldMessagesForChatId hard fail');
        }

        ToastActions.showErrorToast('Error loading chat message');
      });
  },

  updateChatsMessages(chatsMessages: { [key: string]: ChatMessages }) {
    dispatcher.dispatch({
      type: ActionTypes.UPDATE_CHATS_MESSAGES,
      chatsMessages,
    });
  },

  fillGap: memoize(
    (gap: ChatGapMessage) => {
      ChatMessages.yieldMessagesForGap(gap)
        .then((messages) => {
          dispatcher.dispatch({
            type: ActionTypes.LOAD_GAP_MESSAGE,
            chatId: gap.getChatId(),
            gapId: gap.id,
            messages,
          });
        })
        .catch((e) => {
          console.debug(e, e && e.stack);

          if (!isAPIError(e)) {
            console.error('ChatMessages.fillGap hard fail');
          }

          ToastActions.showErrorToast('Error loading chat messages');
        });
    },
    {
      maxAge: DATA_RELOAD_TIMEOUT_MS,
    }
  ),
};

export default ChatMessageActions;
